<template>
  <AuthCard>
    <template #title>
      Enter Your account
    </template>

    <template #form>
      <form
        @submit="onSubmit"
        :class="$style.form"
      >
        <UiField
          type="email"
          label="Email"
          name="email"
          placeholder="Email"
          :rounded="false"
          @input="hideCode"
        />

        <UiField
          type="password"
          :label="t('auth.signin.password')"
          name="password"
          :placeholder="t('auth.signin.password')"
          :rounded="false"
        />

        <UiField
          v-if="showCode"
          :label="t('auth.signin.code')"
          name="code"
          :placeholder="t('auth.signin.code')"
          :rounded="false"
          @input="btnEnabled"
        />

        <UiButton
          :loading="loader"
          :disabled="disableBtn"
        >
          <template #label>
            {{ t('auth.signin.button') }}
          </template>
        </UiButton>
      </form>

      <UiButton
        :class="$style.googleButton"
        color="outline"
        @click="signInWithGoogle"
      >
        <template #label>
          <IconsGoogle :class="$style.googleIcon" />
          {{ t('auth.signin.google') }}
        </template>
      </UiButton>

      <UiButton
        :class="$style.appleButton"
        color="outline"
        @click="signInWithApple"
      >
        <template #label>
          <IconsApple :class="$style.appleIcon" />
          {{ t('auth.signin.apple') }}
        </template>
      </UiButton>

      <div :class="$style.caption">
        <nuxt-link
          :to="localePath('/auth/signup')"
          :class="$style.createAccountLink"
        >
        {{ t('auth.signin.create') }}
        </nuxt-link>
        <nuxt-link :to="localePath('/auth/reset-password')">
          {{ t('auth.signin.forgot') }}
        </nuxt-link>
      </div>
    </template>
  </AuthCard>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { filterPayload } from '@/utils/filterPayload.js';
import type { SignInPayloadDto, SignInResponseDto } from '~/dto';
import { useI18n } from 'vue-i18n';
import { identifyUser } from '~/utils/analyticServices';

const { t } = useI18n();
const localePath = useLocalePath();
useHead({
  title: t('meta.auth.signin.title'),
});

const toastStore = useToastStore();
const { $auth } = useNuxtApp() as any;
const {
  setUserData,
} = useAuthStore();
const {
  patchSubscrb,
} = useSubsrbStore();

const { $moment } = useNuxtApp();
const { $mixpanel } = useNuxtApp();

const showCode = ref<boolean>(false);
const disableBtn = ref<boolean>(false);
const hideCode = () => {
  if (showCode.value) {
    showCode.value = false;
  }
};
const btnEnabled = () => {
  if (disableBtn.value) {
    disableBtn.value = false;
  }
};

const handleAuthSuccess = (data: SignInResponseDto): void => {
  const { token, user, subscription } = data;

  if (import.meta.client) {
    $mixpanel.track('finish_autorization', {
      device: 'false',
      email: user.email,
      user_id: user._id,
      subscription: (subscription as any)?.plan?.name || '',
      exp_date: subscription?.expiresAt || '',
      lang: window.navigator.language,
      utmSource: localStorage.getItem('utmSource'),
      utmMedium: localStorage.getItem('utmMedium'),
      utmCampaign: localStorage.getItem('utmCampaign'),
      utmContent: localStorage.getItem('utmContent'),
      utmTerm: localStorage.getItem('utmTerm'),
      refId: localStorage.getItem('refId'),
    });

    identifyUser(user?.email, {
      '$email': user?.email,
      '$name': user?.email.split('@')[0],
      '$created': new Date().toISOString(),
    });
  }

  $auth.setToken(token);
  setUserData(user);
  patchSubscrb(subscription || null);
  navigateTo($localePath('/account'));
};

const schema: yup.AnyObject = yup.object({
  email: yup.string().email(t('errors.schema.email.invalid')).required(t('errors.schema.email.req')),
  password: yup
    .string()
    .required(t('errors.schema.required')),
});

const loader = ref<boolean>(false);
const { handleSubmit } = useForm({
  validationSchema: schema,
});

const userPayloadKeys = ['email', 'password', 'code'] as (keyof SignInPayloadDto)[];
const { $localePath } = useNuxtApp();

const onSubmit = handleSubmit(
  async (values): Promise<void> => {
    try {
      loader.value = true;
      const payload = filterPayload(values, userPayloadKeys) as SignInPayloadDto;
      const authApi = useNuxtApp().$authApi;

      const { result, data } = await authApi.signInApi(payload);

      if (result) {
        handleAuthSuccess(data);
      }

      if (data.statusCode === 206 && data.message === 'twoFactorAuthAccess') {
        toastStore.showSuccess({
          text: t('auth.signin.twoFactorAuth.access', {
            email: payload.email,
          }),
          duration: 3000,
        });
        showCode.value = true;
        disableBtn.value = true;
      }

      if (data.statusCode === 403 && data.message === 'twoFactorAuthDenied') {
        toastStore.showDanger({
          text: t('auth.signin.twoFactorAuth.denied'),
          duration: 3000,
        });
      }

      if (data.statusCode === 400 && data.message === 'emailAuthCodeTTL') {
        toastStore.showDanger({
          text: t('auth.signin.emailAuthCodeTTL'),
          duration: 3000,
        });
      }

      if (data.statusCode === 400 && data.message.includes('must be 6')) {
        toastStore.showDanger({
          text: t('auth.signin.twoFactorAuth.denied'),
          duration: 3000,
        });
      }

      if (data.statusCode === 401) {
        toastStore.showDanger({
          text: t('errors.signIn.incorrect'),
          duration: 3000,
        });
      }

    } catch (error) {
      console.error(error);
      toastStore.showDanger({
        text: t('errors.signIn.incorrect'),
        duration: 3000,
      });
    } finally {
      loader.value = false;
    }
  }
);

const signInWithGoogle = async (): Promise<void> => {
  try {
    const config = useRuntimeConfig();
    const baseUrl = config.public.apiBaseUrl || 'https://api.stage0detect.com';

    const response = await fetch(`${baseUrl}session/google`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to get Google auth URL');
    }

    const data = await response.json() as { url: string, connectToken: string };
    const { url, connectToken } = data;

    if (!url || !connectToken) {
      throw new Error('Invalid Google auth response');
    }

    if (typeof window === 'undefined') {
      return;
    }

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const authWindow = window.open(
      url,
      'google-auth',
      `width=${width},height=${height},left=${left},top=${top}`,
    );

    if (!authWindow) {
      throw new Error('Google auth popup blocked');
    }

    const checkInterval = window.setInterval(async () => {
      if (authWindow.closed) {
        window.clearInterval(checkInterval);

        try {
          const fetchResponse = await fetch(`${baseUrl}session/google/fetch/${connectToken}`, {
            credentials: 'include',
          });

          if (!fetchResponse.ok) {
            throw new Error('Failed to fetch Google auth data');
          }

          const authData = await fetchResponse.json() as SignInResponseDto;

          if (!authData?.token || !authData?.user) {
            throw new Error('Invalid Google auth data');
          }

          handleAuthSuccess(authData);
        } catch (error) {
          console.error(error);
          try {
            await fetch(`${baseUrl}session/google/cancel/${connectToken}`, {
              method: 'DELETE',
              credentials: 'include',
            });
          } catch {
            // ignore cancel errors
          }
          toastStore.showDanger({
            text: t('errors.googleAuthFailed'),
            duration: 3000,
          });
        }
      }
    }, 500);
  } catch (error) {
    console.error('Error fetching the Google auth URL:', error);
    toastStore.showDanger({
      text: t('errors.googleAuthFailed'),
      duration: 3000,
    });
  }
};

const signInWithApple = async (): Promise<void> => {
  try {
    const config = useRuntimeConfig();
    const baseUrl = config.public.apiBaseUrl || 'https://api.stage0detect.com';

    const response = await fetch(`${baseUrl}session/apple`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to get Apple auth URL');
    }

    const data = await response.json() as { url: string, connectToken: string };
    const { url, connectToken } = data;

    if (!url || !connectToken) {
      throw new Error('Invalid Apple auth response');
    }

    if (typeof window === 'undefined') {
      return;
    }

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const authWindow = window.open(
      url,
      'apple-auth',
      `width=${width},height=${height},left=${left},top=${top}`,
    );

    if (!authWindow) {
      throw new Error('Apple auth popup blocked');
    }

    const checkInterval = window.setInterval(async () => {
      if (authWindow.closed) {
        window.clearInterval(checkInterval);

        const fetchUrl = `${baseUrl}session/apple/fetch/${connectToken}`;

        try {
          const fetchResponse = await fetch(fetchUrl, {
            credentials: 'include',
          });

          if (!fetchResponse.ok) {
            throw new Error('Failed to fetch Apple auth data');
          }

          const authData = await fetchResponse.json() as SignInResponseDto;

          if (!authData?.token || !authData?.user) {
            throw new Error('Invalid Apple auth data');
          }

          handleAuthSuccess(authData);
        } catch (error) {
          console.error(error);
          try {
            await fetch(`${baseUrl}session/apple/cancel/${connectToken}`, {
              method: 'DELETE',
              credentials: 'include',
            });
          } catch {
            // ignore cancel errors
          }
          toastStore.showDanger({
            text: t('errors.appleAuthFailed'),
            duration: 3000,
          });
        }
      }
    }, 500);
  } catch (error) {
    console.error('Error fetching the Apple auth URL:', error);
    toastStore.showDanger({
      text: t('errors.appleAuthFailed'),
      duration: 3000,
    });
  }
};
</script>

<style lang="scss" module>
  .form {
    display: flex;
    flex-direction: column;
  }

  .googleButton {
    margin-top: 16px;
    width: 100%;
  }

  .appleButton {
    margin-top: 12px;
    width: 100%;

    svg {
      width: 29px !important;
      height: 29px !important;
    }
  }

  .googleIcon,
  .appleIcon {
    display: block;
  }

  .caption {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 15px;

    a {
      color: #293EFF;
      text-decoration: none;
      transition: 200ms ease;

      &:hover {
        color: #293EFF;
        opacity: .8;
      }
    }
  }

  a.createAccountLink {
    color: #596269;
  }
</style>